'use client';

import { Button } from '../../components/button';
import { Tooltip } from '../../components/tooltip';
import {
  BodyDefault,
  BodyStrong,
  MetaDefault,
  MonoSmall,
} from '../../components/typography';
import { Box, HStack, VStack } from '@terra/ui-styled-system';
import { formatDistanceToNowStrict } from 'date-fns';
import { ArrowUpRight01Icon } from '@terra/ui-icons';
import { useTranslations } from 'next-intl';
import {
  extractHealthIssueDisplayValues,
  getHealthIssueTranslationKey,
} from './health-issue-display';
import type { ApplicationHealthIssue } from './types';

type ApplicationHealthIssueRowProps = {
  issue: ApplicationHealthIssue;
  canManageApplications?: boolean;
  showBottomBorder?: boolean;
  onFixIssue?: ({
    issueId,
    type,
  }: {
    issueId: string;
    type: ApplicationHealthIssue['type'];
  }) => void;
};

export function ApplicationHealthIssueRow({
  issue,
  canManageApplications = true,
  showBottomBorder = true,
  onFixIssue,
}: ApplicationHealthIssueRowProps) {
  const tWidget = useTranslations(
    'designSystem.notificationCenter.healthIssuesWidget',
  );
  const tIssues = useTranslations(
    'designSystem.notificationCenter.healthIssuesWidget.issues',
  );
  const translationKey = getHealthIssueTranslationKey({ type: issue.type });
  const displayValues = extractHealthIssueDisplayValues({
    type: issue.type,
    context: issue.context,
  });
  const createdAtLabel = formatDistanceToNowStrict(issue.createdAt, {
    addSuffix: true,
  });
  const description = tIssues(`${translationKey}.description`, displayValues);
  const title = tIssues(`${translationKey}.title`, displayValues);
  const handleFixClick = () => {
    onFixIssue?.({ issueId: issue.id, type: issue.type });
  };

  return (
    <HStack
      gap={3}
      alignItems="flex-start"
      w="full"
      py={2}
      px={3}
      borderBottomWidth={showBottomBorder ? '1px' : '0'}
      borderBottomStyle="solid"
      borderBottomColor="border.neutral.subtle"
    >
      <VStack gap={1} alignItems="flex-start" flex={1} minW={0}>
        <HStack gap={2} alignItems="center" w="full" minW={0} overflow="hidden">
          <HStack
            gap={1}
            alignItems="center"
            flex={1}
            minW={0}
            overflow="hidden"
          >
            <Tooltip title={title}>
              <Box minW={0} flex="0 1 auto" overflow="hidden">
                <BodyStrong color="fg.neutral.default" truncate>
                  {title}
                </BodyStrong>
              </Box>
            </Tooltip>
            <MonoSmall
              color="fg.neutral.muted"
              whiteSpace="nowrap"
              flexShrink={0}
            >
              {createdAtLabel}
            </MonoSmall>
          </HStack>
        </HStack>
        <Tooltip
          title={description}
          whiteSpace="normal"
          positioning={{ strategy: 'fixed' }}
        >
          <Box w="full" minW={0}>
            <BodyDefault color="fg.neutral.muted" w="full" lineClamp={3}>
              {description}
            </BodyDefault>
          </Box>
        </Tooltip>
      </VStack>

      <Box flexShrink={0} alignSelf="center">
        {canManageApplications ? (
          <Button variant="outline" size="sm" onClick={handleFixClick}>
            <HStack gap={1} alignItems="center" color="fg.neutral.muted">
              {tWidget('fix')}
              <ArrowUpRight01Icon size={16} />
            </HStack>
          </Button>
        ) : (
          <MetaDefault color="fg.neutral.subtle" whiteSpace="nowrap">
            {tWidget('contactAdministrator')}
          </MetaDefault>
        )}
      </Box>
    </HStack>
  );
}
